import { CalculateHelper } from './calculateHelper';
import { Curve2dUtils } from '@/editor/math/Curve2dUtils';
import { Line2d } from '@/editor/math/Line2d';
import { LineSeg2d } from '@/editor/math/LineSeg2d';
import { EPointOnSegmentType } from '@/editor/types';
import { Point2dUtils } from '@/editor/math/Point2dUtils';
import { RelativeWallInfo, RelativeWallInfoItem } from '@/editor/data/wall/Wall';
import { ISimpleUniformShapeWallInfo, IWallInfo, IWallShape } from '@/editor/types/wallShape';
import { getBaseShape, getWallDrawShape } from '.';
import { ArcSeg2d } from '@/editor/math/arcSeg2d';
import { ERelationToSeg, IShapeSeg2d } from '@/editor/types/shape';
import { Circle2d } from '@/editor/math/circle2d';
import { segRelations } from '@/editor/math/shape/relation';
import { intersectCommonShape } from '@/editor/math/shape/intesection';
import { HandleType } from '../wallShape/calculateHelper';
import { isArc } from '../arc';
import { isStrictlyInsideBand } from './geometry';
import { onWallOf } from './wallLookup';
import { Ray2d } from '@/editor/math/ray2d';
import { IPoint2d } from '@/editor/math/IPoint2d';
import { thresholdValue } from '@/editor/math/const';

interface IWallStore {
  getUniformShapeWallInfos(): ISimpleUniformShapeWallInfo[]; // 获取基础的墙体信息
  updateWallShape(wallId: string, drawInfo: IWallShape): void;
}

/** 端盖局部性倍数，与 calculateHelper 里各处 `thickness * 6` 的 maxReach / cornerReach 取同一量级。 */
const LOCAL_EXIT_REACH = 6;

/** 「端点埋在实心带内」这类关系的四个桶。只有本文件填充，故在本文件内按非可选处理。 */
interface IBodyBuriedBuckets {
  mainStartInWallBody: RelativeWallInfoItem[];
  mainEndInWallBody: RelativeWallInfoItem[];
  startPointInMainWallBody: RelativeWallInfoItem[];
  endPointInMainWallBody: RelativeWallInfoItem[];
}

/**
 * 从 from 沿指向 toward 的方向走 limit 之内能否穿出 earlier 的实心带。
 * 近平行时穿出点会远到几千毫米，端盖沿更早轮廓一路拼过去会甩出本墙跨度之外，故只认局部穿出。
 */
function exitsBandWithin(earlier: IWallInfo, from: IPoint2d, toward: IPoint2d, limit: number): boolean {
  const ray = new Ray2d(from, Point2dUtils.getUnitDirection(from, toward));
  for (const edge of [earlier.leftSeg, earlier.rightSeg]) {
    const points = intersectCommonShape(ray, edge);
    if (!points) continue;
    for (const point of points) {
      const distance = Point2dUtils.distanceTo(from, point);
      if (distance > thresholdValue && distance <= limit) return true;
    }
  }
  return false;
}

/**
 * later 的起点/终点是否埋在 earlier 的实心带内、且本墙能就近穿出去。
 * 用严格版：共端点与落在偏移边线上(垂距=半厚)都不算埋着，普通斜接/T 型接不会被误收。
 */
function buriedEndsIn(earlier: IWallInfo, later: IWallInfo): { start: boolean; end: boolean } {
  if (earlier.thickness <= 0) return { start: false, end: false };
  const halfWidth = earlier.thickness / 2;
  const reach = later.thickness * LOCAL_EXIT_REACH; // 与 calculateHelper 各处端盖局部性闸同一常量
  const buried = (from: IPoint2d, toward: IPoint2d) =>
    isStrictlyInsideBand(from, earlier.seg, halfWidth) && exitsBandWithin(earlier, from, toward, reach);
  return { start: buried(later.start, later.end), end: buried(later.end, later.start) };
}

export class SeparateWallShapeCalculate {
  private _store: IWallStore;
  private _mainWallInfos: IWallInfo[] = [];
  private _calculateHelper: CalculateHelper;
  private _wallInfos: IWallInfo[] = [];
  private _wallIndexMap: Map<string, number> = new Map();

  constructor(store: IWallStore) {
    this._store = store;
    this._calculateHelper = new CalculateHelper();
  }

  // skipCalculate 为 true 不计算连接关系
  getConnectedWallInfo(mainWallId: string) {
    const wallInfos = this._wallInfos;
    if (!wallInfos.length) return;
    const mainWallData = this._wallInfos.find((wallInfo) => wallInfo.id === mainWallId);
    if (!mainWallData) return;

    const { start, end, center, clockwise } = mainWallData;
    // const mainSeg = new LineSeg2d(start, end);
    const mainSeg: IShapeSeg2d =
      center && clockwise !== undefined
        ? new ArcSeg2d({ start, end, center, clockwise: clockwise })
        : new LineSeg2d(start, end);
    const mainStartPoint = Point2dUtils.clone(start);
    const mainEndPoint = Point2dUtils.clone(end);

    const mainStartConnectToPoint: RelativeWallInfoItem[] = []; // 主墙体起始点与墙体端点相连的墙体
    let mainStartConnectToPointOrder: number = 0; // 主墙体所属顺序
    const mainEndConnectToPoint: RelativeWallInfoItem[] = []; // 主墙体终点与墙体端点相连的墙体
    let mainEndConnectToPointOrder: number = 0; // 主墙体所属顺序

    const mainStartOnWallInternal: RelativeWallInfoItem[] = []; // 主墙体起始点在墙体上的墙体(不包含端点处的连接)
    const mainEndOnWallInternal: RelativeWallInfoItem[] = []; // 主墙体终点在墙体上的墙体(不包含端点处的连接)

    const startPointOnMainWallInternal: RelativeWallInfoItem[] = []; // 端点在主墙体内的墙体(不包含端点处的连接)
    const endPointOnMainWallInternal: RelativeWallInfoItem[] = []; // 端点在主墙体内的墙体(不包含端点处的连接)

    const collinear: RelativeWallInfoItem[] = []; // 与主墙体端点共线的墙体,不包含mainStartOnWall,mainEndOnWall,mainStartConnectToPoint,mainEndConnectToPoint中的墙体
    const intersectionInternal: RelativeWallInfoItem[] = []; // 与主墙体端点相交的墙体(不包含端点处的连接)
    const same: RelativeWallInfoItem[] = []; // 与主墙体端点重合的墙体

    const mainStartInWallBody: RelativeWallInfoItem[] = []; // 主墙起点埋在其实心带内的更早墙体
    const mainEndInWallBody: RelativeWallInfoItem[] = []; // 主墙终点埋在其实心带内的更早墙体
    const startPointInMainWallBody: RelativeWallInfoItem[] = []; // 起点埋在主墙实心带内的更晚墙体
    const endPointInMainWallBody: RelativeWallInfoItem[] = []; // 终点埋在主墙实心带内的更晚墙体
    const mainOrder = this._wallIndexOf(mainWallId);

    for (const wd of wallInfos) {
      let relations = segRelations(mainSeg, wd.seg);
      if (!Array.isArray(relations)) relations = [relations];
      relations.forEach((relation) => {
        switch (relation) {
          case ERelationToSeg.Same:
            same.push({
              ...wd,
              connectPoint: null,
              connectType: EPointOnSegmentType.Empty
            });

            mainStartConnectToPointOrder = mainStartConnectToPoint.length;
            mainEndConnectToPointOrder = mainEndConnectToPoint.length;
            break;
          case ERelationToSeg.MainStartStartOverlap:
            mainStartConnectToPoint.push({
              ...wd,
              connectPoint: mainStartPoint,
              connectType: EPointOnSegmentType.StartPoint
            });
            break;
          case ERelationToSeg.MainStartEndOverlap:
            mainStartConnectToPoint.push({
              ...wd,
              connectPoint: mainStartPoint,
              connectType: EPointOnSegmentType.EndPoint
            });
            break;
          case ERelationToSeg.MainEndStartOverlap:
            mainEndConnectToPoint.push({
              ...wd,
              connectPoint: mainEndPoint,
              connectType: EPointOnSegmentType.StartPoint
            });
            break;
          case ERelationToSeg.MainEndEndOverlap:
            mainEndConnectToPoint.push({
              ...wd,
              connectPoint: mainEndPoint,
              connectType: EPointOnSegmentType.EndPoint
            });
            break;
          case ERelationToSeg.MainStartOnSegInternal:
            mainStartOnWallInternal.push({
              ...wd,
              connectPoint: mainStartPoint,
              connectType: EPointOnSegmentType.Between
            });
            break;
          case ERelationToSeg.MainEndOnSegInternal:
            mainEndOnWallInternal.push({
              ...wd,
              connectPoint: mainEndPoint,
              connectType: EPointOnSegmentType.Between
            });
            break;
          case ERelationToSeg.MainSegContainStartInternal:
            startPointOnMainWallInternal.push({
              ...wd,
              connectPoint: wd.start,
              connectType: EPointOnSegmentType.StartPoint
            });
            break;
          case ERelationToSeg.MainSegContainEndInternal:
            endPointOnMainWallInternal.push({
              ...wd,
              connectPoint: wd.end,
              connectType: EPointOnSegmentType.EndPoint
            });
            break;
          case ERelationToSeg.Overlap:
            collinear.push({
              ...wd,
              connectPoint: null,
              connectType: EPointOnSegmentType.Empty
            });
            break;
          case ERelationToSeg.IntersectInternal:
            {
              const ps = intersectCommonShape(mainSeg, wd.seg);
              const connectPoint = ps ? ps[0] : null; // TODO: 待修复，处理多交点情况
              intersectionInternal.push({
                ...wd,
                connectPoint,
                connectType: EPointOnSegmentType.Between
              });
            }
            break;
        }
      });

      const wdOrder = this._wallIndexOf(wd.id);
      if (wdOrder < mainOrder) {
        const buried = buriedEndsIn(wd, mainWallData);
        if (buried.start) {
          mainStartInWallBody.push({ ...wd, connectPoint: mainStartPoint, connectType: EPointOnSegmentType.Between });
        }
        if (buried.end) {
          mainEndInWallBody.push({ ...wd, connectPoint: mainEndPoint, connectType: EPointOnSegmentType.Between });
        }
      } else if (wdOrder > mainOrder) {
        const buried = buriedEndsIn(mainWallData, wd);
        if (buried.start) {
          startPointInMainWallBody.push({ ...wd, connectPoint: wd.start, connectType: EPointOnSegmentType.StartPoint });
        }
        if (buried.end) {
          endPointInMainWallBody.push({ ...wd, connectPoint: wd.end, connectType: EPointOnSegmentType.EndPoint });
        }
      }
    }

    return {
      collinear,
      mainStartConnectToPoint,
      mainStartConnectToPointOrder,
      mainEndConnectToPoint,
      mainEndConnectToPointOrder,
      mainStartOnWallInternal,
      mainEndOnWallInternal,
      startPointOnMainWallInternal,
      endPointOnMainWallInternal,
      intersectionInternal,
      mainStartInWallBody,
      mainEndInWallBody,
      startPointInMainWallBody,
      endPointInMainWallBody,
      same
    };
  }

  // 一次性计算所有墙体关系，提高计算效率
  getConnectedWallInfos() {
    const wallInfos = this._wallInfos;
    if (!wallInfos.length) return [];

    const relativeWallInfos = wallInfos.map((wallInfo): RelativeWallInfo & IBodyBuriedBuckets & { id: string } => {
      return {
        id: wallInfo.id,
        mainStartConnectToPoint: [], // 主墙体起始点与墙体端点相连的墙体
        mainStartConnectToPointOrder: 0, // 主墙体所属顺序
        mainEndConnectToPoint: [], // 主墙体终点与墙体端点相连的墙体
        mainEndConnectToPointOrder: 0, // 主墙体所属顺序

        mainStartOnWallInternal: [], // 主墙体起始点在墙体上的墙体(不包含端点处的连接)
        mainEndOnWallInternal: [], // 主墙体终点在墙体上的墙体(不包含端点处的连接)
        startPointOnMainWallInternal: [], // 端点在主墙体内的墙体(不包含端点处的连接)
        endPointOnMainWallInternal: [], // 端点在主墙体内的墙体(不包含端点处的连接)
        intersectionInternal: [], // 与主墙体端点相交的墙体(不包含端点处的连接)

        mainStartInWallBody: [], // 主墙起点埋在其实心带内的更早墙体
        mainEndInWallBody: [], // 主墙终点埋在其实心带内的更早墙体
        startPointInMainWallBody: [], // 起点埋在主墙实心带内的更晚墙体
        endPointInMainWallBody: [], // 终点埋在主墙实心带内的更晚墙体

        collinear: [], // 与主墙体端点共线的墙体,不包含mainStartOnWall,mainEndOnWall,mainStartConnectToPoint,mainEndConnectToPoint中的墙体
        same: [] // 与主墙体端点重合的墙体
      };
    });

    for (let i = 0; i < wallInfos.length; i++) {
      const mainWallInfo = wallInfos[i];
      const mainRelativeWallInfo = relativeWallInfos[i];
      mainRelativeWallInfo.same.push({
        ...mainWallInfo,
        connectPoint: null,
        connectType: EPointOnSegmentType.Empty
      });
      mainRelativeWallInfo.mainStartConnectToPointOrder = mainRelativeWallInfo.mainStartConnectToPoint.length;
      mainRelativeWallInfo.mainEndConnectToPointOrder = mainRelativeWallInfo.mainEndConnectToPoint.length;

      for (let j = i + 1; j < relativeWallInfos.length; j++) {
        const subWallInfo = wallInfos[j];
        const subRelativeWallInfo = relativeWallInfos[j];
        let relations = segRelations(mainWallInfo.seg, subWallInfo.seg);
        if (!Array.isArray(relations)) relations = [relations];

        relations.forEach((relation) => {
          switch (relation) {
            case ERelationToSeg.Same:
              // 目前墙体不支持重合，所以不处理
              break;
            case ERelationToSeg.MainStartStartOverlap:
              mainRelativeWallInfo.mainStartConnectToPoint.push({
                ...subWallInfo,
                connectPoint: mainWallInfo.start,
                connectType: EPointOnSegmentType.StartPoint
              });
              subRelativeWallInfo.mainStartConnectToPoint.push({
                ...mainWallInfo,
                connectPoint: mainWallInfo.start,
                connectType: EPointOnSegmentType.StartPoint
              });
              break;
            case ERelationToSeg.MainStartEndOverlap:
              mainRelativeWallInfo.mainStartConnectToPoint.push({
                ...subWallInfo,
                connectPoint: mainWallInfo.start,
                connectType: EPointOnSegmentType.EndPoint
              });
              subRelativeWallInfo.mainEndConnectToPoint.push({
                ...mainWallInfo,
                connectPoint: mainWallInfo.start,
                connectType: EPointOnSegmentType.StartPoint
              });
              break;
            case ERelationToSeg.MainEndStartOverlap:
              mainRelativeWallInfo.mainEndConnectToPoint.push({
                ...subWallInfo,
                connectPoint: mainWallInfo.end,
                connectType: EPointOnSegmentType.StartPoint
              });
              subRelativeWallInfo.mainStartConnectToPoint.push({
                ...mainWallInfo,
                connectPoint: mainWallInfo.end,
                connectType: EPointOnSegmentType.EndPoint
              });
              break;
            case ERelationToSeg.MainEndEndOverlap:
              mainRelativeWallInfo.mainEndConnectToPoint.push({
                ...subWallInfo,
                connectPoint: mainWallInfo.end,
                connectType: EPointOnSegmentType.EndPoint
              });
              subRelativeWallInfo.mainEndConnectToPoint.push({
                ...mainWallInfo,
                connectPoint: mainWallInfo.end,
                connectType: EPointOnSegmentType.EndPoint
              });
              break;
            case ERelationToSeg.MainStartOnSegInternal:
              mainRelativeWallInfo.mainStartOnWallInternal.push({
                ...subWallInfo,
                connectPoint: mainWallInfo.start,
                connectType: EPointOnSegmentType.Between
              });
              subRelativeWallInfo.startPointOnMainWallInternal.push({
                ...mainWallInfo,
                connectPoint: mainWallInfo.start,
                connectType: EPointOnSegmentType.StartPoint
              });
              break;
            case ERelationToSeg.MainEndOnSegInternal:
              mainRelativeWallInfo.mainEndOnWallInternal.push({
                ...subWallInfo,
                connectPoint: mainWallInfo.end,
                connectType: EPointOnSegmentType.Between
              });
              subRelativeWallInfo.startPointOnMainWallInternal.push({
                ...mainWallInfo,
                connectPoint: mainWallInfo.end,
                connectType: EPointOnSegmentType.EndPoint
              });
              break;
            case ERelationToSeg.MainSegContainStartInternal:
              mainRelativeWallInfo.startPointOnMainWallInternal.push({
                ...subWallInfo,
                connectPoint: subWallInfo.start,
                connectType: EPointOnSegmentType.StartPoint
              });
              subRelativeWallInfo.mainStartOnWallInternal.push({
                ...mainWallInfo,
                connectPoint: subWallInfo.start,
                connectType: EPointOnSegmentType.Between
              });
              break;
            case ERelationToSeg.MainSegContainEndInternal:
              mainRelativeWallInfo.endPointOnMainWallInternal.push({
                ...subWallInfo,
                connectPoint: subWallInfo.end,
                connectType: EPointOnSegmentType.EndPoint
              });
              subRelativeWallInfo.mainEndOnWallInternal.push({
                ...mainWallInfo,
                connectPoint: subWallInfo.end,
                connectType: EPointOnSegmentType.EndPoint
              });
              break;
            case ERelationToSeg.Overlap:
              mainRelativeWallInfo.collinear.push({
                ...subWallInfo,
                connectPoint: null,
                connectType: EPointOnSegmentType.Empty
              });
              subRelativeWallInfo.collinear.push({
                ...mainWallInfo,
                connectPoint: null,
                connectType: EPointOnSegmentType.Empty
              });
              break;
            case ERelationToSeg.IntersectInternal:
              {
                const ps = intersectCommonShape(mainWallInfo.seg, subWallInfo.seg);
                const connectPoint = ps ? ps[0] : null; // TODO: 待修复，处理多交点情况

                mainRelativeWallInfo.intersectionInternal.push({
                  ...subWallInfo,
                  connectPoint,
                  connectType: EPointOnSegmentType.Between
                });
                subRelativeWallInfo.intersectionInternal.push({
                  ...mainWallInfo,
                  connectPoint,
                  connectType: EPointOnSegmentType.Between
                });
              }
              break;
          }
        });

        // 数组下标即建立顺序，故 i 恒早于 j：只需判「主墙(早)埋住子墙(晚)的端点」一个方向
        const buried = buriedEndsIn(mainWallInfo, subWallInfo);
        if (buried.start) {
          subRelativeWallInfo.mainStartInWallBody.push({
            ...mainWallInfo,
            connectPoint: subWallInfo.start,
            connectType: EPointOnSegmentType.Between
          });
          mainRelativeWallInfo.startPointInMainWallBody.push({
            ...subWallInfo,
            connectPoint: subWallInfo.start,
            connectType: EPointOnSegmentType.StartPoint
          });
        }
        if (buried.end) {
          subRelativeWallInfo.mainEndInWallBody.push({
            ...mainWallInfo,
            connectPoint: subWallInfo.end,
            connectType: EPointOnSegmentType.Between
          });
          mainRelativeWallInfo.endPointInMainWallBody.push({
            ...subWallInfo,
            connectPoint: subWallInfo.end,
            connectType: EPointOnSegmentType.EndPoint
          });
        }
      }
    }

    return relativeWallInfos;
  }

  private convertToCompleteWallInfo(simpleWallInfos: ISimpleUniformShapeWallInfo[]) {
    return simpleWallInfos.map((item) => {
      if (!item.drawShape.length) {
        const { baseShape, startShape, endShape } = getBaseShape(item);
        // 直接修改了，可能有风险
        item.drawShape = baseShape;
        item.startShape = startShape;
        item.endShape = endShape;
      }

      // 弧墙
      if (isArc(item)) {
        const arc = new ArcSeg2d({ start: item.start, end: item.end, center: item.center, clockwise: item.clockwise });
        const leftRadiusOffset = (item.thickness / 2) * (arc.clockwise ? 1 : -1);
        const leftArc = arc.getOffsetArc(leftRadiusOffset);
        const rightArc = arc.getOffsetArc(-leftRadiusOffset);

        const circle = new Circle2d({ center: arc.center, radius: arc.radius });
        const leftCircle = new Circle2d({ center: arc.center, radius: leftArc.radius });
        const rightCircle = new Circle2d({ center: arc.center, radius: rightArc.radius });

        return {
          ...item,
          seg: arc,
          leftSeg: leftArc,
          rightSeg: rightArc,

          shape: circle,
          leftShape: leftCircle,
          rightShape: rightCircle
        };
      }

      const seg = new LineSeg2d(item.start, item.end);
      const { leftCurve, rightCurve } = Curve2dUtils.offsetCurves(seg, item.thickness / 2);

      const line = new Line2d({ points: { p1: item.start, p2: item.end } });
      const leftLine = new Line2d({ points: { p1: leftCurve.start, p2: leftCurve.end } });
      const rightLine = new Line2d({ points: { p1: rightCurve.start, p2: rightCurve.end } });
      return {
        ...item,

        seg: seg,
        leftSeg: leftCurve,
        rightSeg: rightCurve,

        shape: line,
        leftShape: leftLine,
        rightShape: rightLine
      };
    });
  }

  /**
   * @param includeSkipWall 是否包含要跳过墙体（添加该参数是为了一些地方获取完整关联关系，后面再优化）
   * @returns
   */
  updateWallInfos(excludeSkipWall: boolean = true) {
    this._wallInfos = this.convertToCompleteWallInfo(
      this._store.getUniformShapeWallInfos().filter((wallInfo) => !excludeSkipWall || !wallInfo.skipCalculate)
    );

    this._wallIndexMap.clear();
    this._wallInfos.forEach((wallInfo, index) => {
      this._wallIndexMap.set(wallInfo.id, index);
    });

    return this;
  }

  setWalls(ids: Set<string>) {
    if (!ids.size || !this._wallInfos.length) return this;

    this._mainWallInfos = [];

    this._wallInfos.forEach((wallInfo) => {
      if (ids.has(wallInfo.id)) this._mainWallInfos.push(wallInfo);
    });

    return this;
  }

  compute() {
    this._calculateHelper.clear();

    if (!this._mainWallInfos.length || !this._wallInfos.length) return this;

    this._mainWallInfos.forEach((wallInfo) => {
      this.addHandleWall(wallInfo.id);
    });

    this._calculateHelper.startChainCalculate();

    return this;
  }

  updateShapes() {
    // 更新绘制信息
    const modifyList = this._calculateHelper.result;
    for (const item of modifyList) {
      const { wallId, type, points } = item;
      const modifyWallInfo = this._wallInfos.find((item) => item.id === wallId);

      if (!modifyWallInfo) continue;

      let startShape = modifyWallInfo.startShape;
      let endShape = modifyWallInfo.endShape;
      if (type === 'start') {
        startShape = [...points];
      } else {
        endShape = [...points];
      }

      modifyWallInfo.startShape = startShape;
      modifyWallInfo.endShape = endShape;

      this._store.updateWallShape(wallId, {
        startShape,
        endShape,
        drawShape: getWallDrawShape(
          {
            start: modifyWallInfo.start,
            end: modifyWallInfo.end,
            center: modifyWallInfo.center,
            clockwise: modifyWallInfo.clockwise
          },
          startShape,
          endShape
        )
      });
    }
  }

  /**
   * 墙体在 _wallInfos 里的下标（即建立顺序，队列按它升序处理）。
   * 相连墙可能不在本次计算范围内（如被 skipCalculate 过滤掉），这类墙没有下标，按「最后处理」计，
   * 与 calculateHelper 里 orderOf 取 Infinity 的约定一致。
   */
  private _wallIndexOf(wallId: string) {
    return this._wallIndexMap.get(wallId) ?? Infinity;
  }

  private addHandleWall(wallId: string) {
    // --- 添加完整的墙体数据 ---
    this._addWallInfoToCache(wallId);
    const wallInfo = this._calculateHelper.cacheWallMap.get(wallId);
    if (!wallInfo) return;

    const mainStartConnectToPoint = wallInfo.relativeWallInfos?.mainStartConnectToPoint || [];
    const mainEndConnectToPoint = wallInfo.relativeWallInfos?.mainEndConnectToPoint || [];

    // 走 onWallOf 而不是直接读桶：端点仅埋在更早墙实心带内(不在其中线上)的那条也要入缓存、并走端点落墙那条队列
    const mainStartOnWallInternal = onWallOf(wallInfo, HandleType.Start);
    const mainEndOnWallInternal = onWallOf(wallInfo, HandleType.End);
    // 并上「端点埋在本墙实心带内」的后建墙：本墙被移到它们身上时，只有在此入队它们的端盖才会跟着重切
    // (stores 侧的失效集合取的是改动【前】的关系，只覆盖得到「移走」那一半)
    const startPointOnMainWallInternal = [
      ...(wallInfo.relativeWallInfos?.startPointOnMainWallInternal || []),
      ...(wallInfo.relativeWallInfos?.startPointInMainWallBody || [])
    ];
    const endPointOnMainWallInternal = [
      ...(wallInfo.relativeWallInfos?.endPointOnMainWallInternal || []),
      ...(wallInfo.relativeWallInfos?.endPointInMainWallBody || [])
    ];

    // --- 起点处理 ---
    if (!mainStartOnWallInternal.length) {
      // --- 纯端点端点连接处理 ---
      this._calculateHelper.addToPointPointConnectedQueue({
        wallId,
        type: HandleType.Start,
        wallIndex: this._wallIndexOf(wallId)
      });
      mainStartConnectToPoint.forEach((item) => {
        this._addWallWithBoundaryNeighborsToCache(item.id);
        this._calculateHelper.addToPointPointConnectedQueue({
          wallId: item.id,
          type: item.connectType === EPointOnSegmentType.StartPoint ? HandleType.Start : HandleType.End,
          wallIndex: this._wallIndexOf(item.id)
        });
      });
    } else {
      // --- 包含端点墙体连接情况的端点处理 ---
      this._calculateHelper.addToPointWallConnectedQueue({
        wallId,
        type: HandleType.Start,
        wallIndex: this._wallIndexOf(wallId)
      });
      mainStartOnWallInternal.forEach((item) => {
        // 不处理这种端点，但需要添加到缓存(连同其一跳邻居边界墙，供绕角端盖使用)
        this._addWallWithBoundaryNeighborsToCache(item.id);
      });
      mainStartConnectToPoint.forEach((item) => {
        this._addWallWithBoundaryNeighborsToCache(item.id);
        this._calculateHelper.addToPointWallConnectedQueue({
          wallId: item.id,
          type: item.connectType === EPointOnSegmentType.StartPoint ? HandleType.Start : HandleType.End,
          wallIndex: this._wallIndexOf(item.id)
        });
      });
    }

    // --- 终点处理 ---
    if (!mainEndOnWallInternal.length) {
      this._calculateHelper.addToPointPointConnectedQueue({
        wallId,
        type: HandleType.End,
        wallIndex: this._wallIndexOf(wallId)
      });

      mainEndConnectToPoint.forEach((item) => {
        this._addWallWithBoundaryNeighborsToCache(item.id);
        this._calculateHelper.addToPointPointConnectedQueue({
          wallId: item.id,
          type: item.connectType === EPointOnSegmentType.StartPoint ? HandleType.Start : HandleType.End,
          wallIndex: this._wallIndexOf(item.id)
        });
      });
    } else {
      this._calculateHelper.addToPointWallConnectedQueue({
        wallId,
        type: HandleType.End,
        wallIndex: this._wallIndexOf(wallId)
      });
      mainEndOnWallInternal.forEach((item) => {
        // 不处理这种端点，但需要添加到缓存(连同其一跳邻居边界墙，供绕角端盖使用)
        this._addWallWithBoundaryNeighborsToCache(item.id);
      });
      mainEndConnectToPoint.forEach((item) => {
        this._addWallWithBoundaryNeighborsToCache(item.id);
        this._calculateHelper.addToPointWallConnectedQueue({
          wallId: item.id,
          type: item.connectType === EPointOnSegmentType.StartPoint ? HandleType.Start : HandleType.End,
          wallIndex: this._wallIndexOf(item.id)
        });
      });
    }

    // --- 其他墙体在主墙体上 ---
    if (startPointOnMainWallInternal.length) {
      startPointOnMainWallInternal.forEach((item) => {
        this._addWallInfoToCache(item.id);
        this._calculateHelper.addToPointWallConnectedQueue({
          wallId: item.id,
          type: item.connectType === EPointOnSegmentType.StartPoint ? HandleType.Start : HandleType.End,
          wallIndex: this._wallIndexOf(item.id)
        });
      });
    }
    if (endPointOnMainWallInternal.length) {
      endPointOnMainWallInternal.forEach((item) => {
        this._addWallInfoToCache(item.id);
        this._calculateHelper.addToPointWallConnectedQueue({
          wallId: item.id,
          type: item.connectType === EPointOnSegmentType.StartPoint ? HandleType.Start : HandleType.End,
          wallIndex: this._wallIndexOf(item.id)
        });
      });
    }
  }

  private _addWallInfoToCache(wallId: string) {
    if (this._calculateHelper.cacheWallMap.has(wallId)) return;
    const currentWallInfo = this._wallInfos.find((wallInfo) => wallInfo.id === wallId); // TODO: 待优化，列表查找耗时
    if (!currentWallInfo) return;

    const relativeWallInfos = this.getConnectedWallInfo(wallId);

    this._calculateHelper.addCacheWall(
      wallId,
      {
        ...currentWallInfo,
        relativeWallInfos
      },
      this._wallIndexMap.get(wallId)
    );
  }

  // 主墙端点搭在某边界墙(薄柱/薄弧带/边界折线的一条边)上时——不论是落在它 body 上，还是与它【端点相连】——除该边界墙
  // 外，还需把它「端点相连的一跳邻居边界墙」也入缓存：端盖绕角(boundaryPocketCut)与并集轮廓闭合
  // (calStraightMultiCutByEarlier 喂给 PathFinder 的切割集)都要靠 relativeWallInfos + _cacheWallMap 取邻边；绘制增量下
  // 只有主墙直接相连的那条边入缓存、邻边缺失 → 绕角失败被裁到无限延长线的远端错误交点，或干脆求不到出口、端盖退化成
  // 未裁切平口(薄弧带端头就是后者：穿出边是弧带另一侧的弧，只与接头那两条边界一跳相连)。补上邻边，使「绘制增量」与
  // 「打开方案全量重算」结果一致。
  private _addWallWithBoundaryNeighborsToCache(wallId: string) {
    this._addWallInfoToCache(wallId);
    const info = this._calculateHelper.cacheWallMap.get(wallId);
    if (!info || info.thickness !== 0) return; // 仅边界墙需要补邻边链
    const rel = info.relativeWallInfos;
    if (!rel) return;
    // 只补【厚 0】邻边：这里要补的是"边界折线的下一段"，把它闭合起来给端盖绕角/并集轮廓用。
    // 有厚隔墙不该从这条路进缓存 —— 它要成为 cutter 得靠自己出现在主墙的相连关系里(那时本来就会入缓存)，
    // 从边界另一头顺带捎进来只会让缓存内容依赖于遍历路径。
    [...(rel.mainStartConnectToPoint || []), ...(rel.mainEndConnectToPoint || [])].forEach((n) => {
      const nw = this._wallInfos.find((w) => w.id === n.id);
      if (nw && nw.thickness === 0) this._addWallInfoToCache(n.id);
    });
  }
}
